import React, { useState, useMemo } from 'react';
import { formatInStoreTimezone } from '../../utils/dateUtils';

const denominations = [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05];

const OpenTillModal = ({ onConfirm, onCancel, allowDirectEntry, lastClosedSession, timezone = 'Australia/Melbourne' }) => {
  const [counts, setCounts] = useState({});
  const [mode, setMode] = useState('denominations');
  const [directAmount, setDirectAmount] = useState('');

  const total = useMemo(() => {
    if (allowDirectEntry && mode === 'direct') {
      const value = Number(directAmount);
      return Number.isFinite(value) && value > 0 ? value : 0;
    }
    return denominations.reduce((acc, den) => {
      return acc + (counts[den] || 0) * den;
    }, 0);
  }, [counts, directAmount, mode, allowDirectEntry]);

  const handleCountChange = (den, count) => {
    setCounts(prev => ({ ...prev, [den]: count }));
  };

  const handleConfirm = () => {
    if (allowDirectEntry && mode === 'direct') {
      onConfirm(total, undefined);
      return;
    }
    onConfirm(total, counts);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start py-8">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Open Till</h2>
        {typeof lastClosedSession?.closingNote === 'string' && lastClosedSession.closingNote.trim() ? (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Last Close Note</p>
            {lastClosedSession?.closingTime ? (
              <p className="text-[11px] text-amber-700 mt-0.5">
                {formatInStoreTimezone(lastClosedSession.closingTime, 'dd-MM-yyyy HH:mm', timezone)}
              </p>
            ) : null}
            <p className="text-sm text-amber-900 mt-1">{lastClosedSession.closingNote}</p>
          </div>
        ) : null}
        {allowDirectEntry && (
          <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-full mb-4">
            <button
              type="button"
              onClick={() => setMode('denominations')}
              className={`flex-1 text-sm font-semibold px-3 py-1.5 rounded-full transition ${
                mode === 'denominations' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
              }`}
            >
              Count denominations
            </button>
            <button
              type="button"
              onClick={() => setMode('direct')}
              className={`flex-1 text-sm font-semibold px-3 py-1.5 rounded-full transition ${
                mode === 'direct' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
              }`}
            >
              Enter total
            </button>
          </div>
        )}
        {allowDirectEntry && mode === 'direct' ? (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Opening float</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={directAmount}
              onChange={(e) => setDirectAmount(e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="0.00"
            />
            <p className="text-xs text-slate-500">Enter the total amount already in the drawer.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {denominations.map(den => (
              <div key={den} className="flex items-center justify-between">
                <span className="font-medium">${den.toFixed(2)}</span>
                <input
                  type="number"
                  value={counts[den] || ''}
                  onChange={(e) => handleCountChange(den, parseInt(e.target.value, 10) || 0)}
                  className="w-24 p-2 border rounded-md"
                />
              </div>
            ))}
          </div>
        )}
        <div className="text-right font-bold text-xl mt-4">
          Total: ${total.toFixed(2)}
        </div>
        <div className="flex justify-end space-x-3 pt-4">
          <button onClick={onCancel} className="bg-gray-200 px-4 py-2 rounded-md">
            Cancel
          </button>
          <button
            onClick={() => {
              handleConfirm();
            }}
            className="bg-primary text-white px-4 py-2 rounded-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpenTillModal;
